import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  keyframes,
  LinearProgress,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  LocalOffer as ServiceIcon,
  Settings as OptionsIcon,
  Person as StaffIcon,
  Schedule as TimeIcon,
  CheckCircle as ConfirmIcon,
  PersonAdd as CustomerIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import MobileStepper from './MobileStepper';

// Define the pulse animation
const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(var(--pulse-color), 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(var(--pulse-color), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--pulse-color), 0);
  }
`;

const steps = [
  {
    label: 'Service',
    description: 'Choose your service',
    icon: ServiceIcon,
    color: '#FF6B6B', // Coral red
  },
  {
    label: 'Additional Options',
    description: 'Customize your booking',
    icon: OptionsIcon,
    color: '#9B59B6', // Purple
  },
  {
    label: 'Date',
    description: 'Choose your booking date',
    icon: TimeIcon,
    color: '#45B7D1', // Sky blue
  },
  {
    label: 'Staff',
    description: 'Select your preferred staff',
    icon: StaffIcon,
    color: '#4ECDC4', // Turquoise
  },
  {
    label: 'Time',
    description: 'Pick a convenient time',
    icon: TimeIcon,
    color: '#45B7D1', // Sky blue
  },
  {
    label: 'Customer',
    description: 'Enter customer details',
    icon: CustomerIcon,
    color: '#FFB347', // Orange
  },
  {
    label: 'Confirm',
    description: 'Review and book',
    icon: ConfirmIcon,
    color: '#96CEB4', // Sage green
  },
];

const BookingStepper = ({ activeStep, isMobile, onStepClick }) => {
  const theme = useTheme();
  const progressPercentage = ((activeStep + 1) / steps.length) * 100;

  // Use the enhanced mobile stepper on mobile devices
  if (isMobile) {
    return <MobileStepper activeStep={activeStep} theme={theme.palette.mode} />;
  }

  const handleStepClick = (stepIndex) => {
    // Allow clicking on completed steps to navigate back
    if (onStepClick && stepIndex < activeStep) {
      onStepClick(stepIndex);
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        background: 'transparent',
        p: isMobile ? 2 : 3,
        border: 'none',
        boxShadow: 'none',
        '& .MuiPaper-root, & .MuiStepper-root, & .MuiStep-root': {
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent !important',
          '&::before, &::after': {
            display: 'none',
          },
        },
      }}
    >
      {/* Progress Indicator */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {Math.round(progressPercentage)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              background: `linear-gradient(90deg, ${steps[activeStep]?.color || theme.palette.primary.main} 0%, ${alpha(steps[activeStep]?.color || theme.palette.primary.main, 0.7)} 100%)`,
              transition: 'width 0.5s ease-in-out',
            },
          }}
        />
      </Box>
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel={!isMobile}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        connector={null}
        sx={{
          border: 'none !important',
          position: 'relative',
          '& .MuiStepLabel-root': {
            padding: isMobile ? '8px 0' : undefined,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: 0.7,
            position: 'relative',
            zIndex: 3,
            border: 'none !important',
            '&:hover': {
              opacity: 1,
            },
            '&.Mui-active': {
              opacity: 1,
              transform: 'scale(1.05)',
            },
            '&.Mui-completed': {
              opacity: 1,
            },
          },
          '& .MuiStepLabel-label': {
            mt: 1,
            fontSize: '0.9rem',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            color: theme.palette.text.secondary,
            position: 'relative',
            zIndex: 3,
            border: 'none !important',
            '&.Mui-active': {
              fontWeight: 700,
              color: theme.palette.text.primary,
            },
            '&.Mui-completed': {
              fontWeight: 600,
              color: theme.palette.text.primary,
            },
          },
          '& .MuiStep-root': {
            border: 'none !important',
            position: 'relative',
            '&::after': {
              display: 'none',
            },
          },
          '& .MuiStepConnector-root': {
            display: 'none',
          },
          '& .MuiPaper-root': {
            border: 'none !important',
            boxShadow: 'none !important',
          },
          '& .MuiStepper-horizontal': {
            border: 'none !important',
            boxShadow: 'none !important',
          },
          '& .MuiStepper-root': {
            border: 'none !important',
            boxShadow: 'none !important',
            backgroundColor: 'transparent !important',
            background: 'transparent !important',
          },
          '& .MuiStepContent-root': {
            backgroundColor: 'transparent !important',
          },
        }}
      >
        {steps.map(({ label, description, icon: Icon, color }, index) => {
          const completed = index < activeStep;
          const active = index === activeStep;
          const isClickable = completed && onStepClick;

          return (
            <Step 
              key={label}
              completed={completed}
              sx={{
                mx: isMobile ? 0 : 1,
                position: 'relative',
              }}
            >
              <Tooltip 
                title={description || label}
                arrow
                placement="top"
              >
                <StepLabel
                  onClick={() => isClickable && handleStepClick(index)}
                  sx={{
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable ? {
                      '& .MuiStepLabel-label': {
                        color: color,
                      },
                    } : {},
                  }}
                  StepIconComponent={({ active: stepActive, completed: stepCompleted }) => (
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 2,
                        background: stepCompleted || stepActive
                          ? `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`
                          : theme => theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.background.paper, 0.8)
                            : alpha('#fff', 0.9),
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: stepActive ? 'scale(1.15)' : stepCompleted ? 'scale(1.05)' : 'scale(1)',
                        boxShadow: stepActive 
                          ? `0 4px 20px ${alpha(color, 0.4)}, 0 0 0 4px ${alpha(color, 0.2)}`
                          : stepCompleted
                          ? `0 2px 12px ${alpha(color, 0.3)}, 0 0 0 2px ${alpha(color, 0.15)}`
                          : theme => `0 2px 8px ${alpha(theme.palette.divider, 0.2)}`,
                        cursor: isClickable ? 'pointer' : 'default',
                        '&:hover': isClickable ? {
                          transform: 'scale(1.1)',
                          boxShadow: `0 4px 16px ${alpha(color, 0.4)}, 0 0 0 4px ${alpha(color, 0.2)}`,
                        } : {},
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: '50%',
                          background: stepCompleted || stepActive
                            ? `linear-gradient(135deg, ${alpha(color, 0.9)} 0%, ${alpha(color, 1)} 100%)`
                            : 'transparent',
                          opacity: stepCompleted || stepActive ? 1 : 0,
                          transition: 'all 0.3s ease',
                        },
                        ...(stepActive && {
                          '--pulse-color': color.replace('#', '')
                            .match(/.{2}/g)
                            .map(hex => parseInt(hex, 16))
                            .join(', '),
                          animation: `${pulseAnimation} 2s infinite`,
                        }),
                      }}
                    >
                      {stepCompleted ? (
                        <CheckIcon
                          sx={{
                            fontSize: 28,
                            color: '#fff',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            zIndex: 3,
                          }}
                        />
                      ) : (
                        <Icon
                          sx={{
                            fontSize: 26,
                            color: stepActive 
                              ? '#fff' 
                              : theme => theme.palette.mode === 'dark' 
                                ? color 
                                : color,
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            zIndex: 3,
                            transform: stepActive ? 'scale(1.1)' : 'scale(1)',
                            opacity: stepActive ? 1 : 0.8,
                          }}
                        />
                      )}
                    </Box>
                  )}
                >
                  {label}
                </StepLabel>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default BookingStepper; 